import type { Module } from '@/shared/schemas';
import { Badge, Tag } from '@/shared/ui';
import { trackProjectVisit } from '../api/track-visit';
import styles from './ModuleCard.module.css';

interface ModuleCardProps {
  readonly module: Module;
  readonly isAuthenticated: boolean;
  readonly userId: string | null;
}

const AUTH_URL = 'https://auth.hey.sh/login';

function LockIcon() {
  return (
    <svg
      className={styles.lockIcon}
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

export function ModuleCard({ module, isAuthenticated, userId }: ModuleCardProps) {
  const isMembersOnly = module.visibility === 'members';
  const isLocked = isMembersOnly && !isAuthenticated;

  const href = isLocked ? `${AUTH_URL}?continue=${encodeURIComponent(module.url)}` : module.url;

  const handleClick = () => {
    if (userId && !isLocked) {
      trackProjectVisit(userId, module.id).catch(() => {
        // Silently ignore tracking errors
      });
    }
  };

  return (
    <article className={`${styles.card} ${isLocked ? styles.locked : ''}`}>
      <a href={href} className={styles.link} rel="noopener noreferrer" onClick={handleClick}>
        <div className={styles.header}>
          <h3 className={styles.name}>{module.name}</h3>
          <div className={styles.badges}>
            <Badge variant={module.status}>{module.status}</Badge>
            <Badge variant={module.visibility}>{module.visibility}</Badge>
          </div>
        </div>
        <p className={styles.desc}>{module.description}</p>
        <div className={styles.tags}>
          {module.tags.map((tag) => (
            <Tag key={tag}>{tag}</Tag>
          ))}
        </div>
        <div className={styles.action}>
          {isLocked ? (
            <span className={styles.lockedAction}>
              <LockIcon />
              Sign in to access
            </span>
          ) : (
            <span>Open</span>
          )}
        </div>
      </a>
    </article>
  );
}
